import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import {
  TakeProfitContext,
  useValidateTakeProfit,
} from '@/App/tiles/orders/TakeProfitOrder/TakeProfitOrderCreationTile/takeProfitContext';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectCustomerPriceFromTakeProfitTile,
  selectTakeProfitErrors,
} from '@/store/orders/takeProfitOrder/selectors.ts';
import { useDebounce } from '@/utils/hooks/debounce.ts';

export const TakeProfitCustomerPrice = () => {
  const track = useTrackEvent();
  const { tileId, setIsModifyingField } = useContext(TakeProfitContext);
  const userContextValue = useUserContext();
  const isInternalSales = userContextValue?.currentUser.userType === 'Internal Sales';
  const value = useAppSelector((state) => selectCustomerPriceFromTakeProfitTile(state, tileId));

  const errors = useAppSelector((state) => selectTakeProfitErrors(state, tileId));
  const customerPriceError = errors?.customerPrice;

  const validateTakeProfit = useValidateTakeProfit();

  const handleCustomerPriceChange = useDebounce((newValue: number | undefined) => {
    track('Order action', 'Customer price changed');
    validateTakeProfit({
      customerPrice: newValue,
    });
  }, 600);

  return (
    <OrderRow labelId={`order.fields.${isInternalSales ? 'customerPrice' : 'limitPrice'}.label`}>
      <OmsFieldErrorWrapper errors={customerPriceError}>
        <SgNumberInput
          valuePosition="start"
          selectAllOnFocus
          value={value}
          maxDecimals={9}
          onChange={handleCustomerPriceChange}
          onFocus={() => setIsModifyingField(true)}
          onBlur={() => setIsModifyingField(false)}
          tabNoIndex
          data-e2e="customer-price"
          size="sm"
          state={customerPriceError ? 'invalid' : undefined}
        />
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
